import { useState, useContext } from 'react';
import { StructurePreview } from './page-structure/StructurePreview';
import { i18n } from 'app/utils/i18n';
import { useSelectedTemplates, useUpdateSelectedTemplates, useTemplates } from 'app/api/template-queries';
import { Spinner, Modal } from 'app/components';
import { useParams } from 'react-router';
import { UserContext } from 'app/state/contexts';
import { usePermissions } from 'app/utils/hooks/document-permission';
import { useDesignDraft } from './design-configuration/hooks/useDesignDraft';

const useDocumentAspectRatio = () => {
  const { style, draftEntry } = useDesignDraft({ styleType: 'graphical-variables', parsed: true });
  const documentVariables = style.data?.content ?? {};
  const draftVariables = draftEntry?.style ?? {};
  const variables = { ...documentVariables, ...draftVariables };
  const w = parseInt(variables.pageWidth, 10) || 794;
  const h = parseInt(variables.pageHeight, 10) || 1123;
  return `${w} / ${h}`;
};

const StructureModal = ({ show, selected, onClose, pageStructures, aspectRatio }) => {
  const { documentId } = useParams();

  const [newSelected, setNewSelected] = useState(selected);
  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState('all');
  const { mutate: saveSelectedTemplates } = useUpdateSelectedTemplates(documentId);

  const handlePreviewClick = (superId) => {
    if (newSelected.includes(superId)) {
      setNewSelected(newSelected.filter((s) => s !== superId));
    } else {
      setNewSelected([...newSelected, superId]);
    }
  };

  const handleSave = () => {
    saveSelectedTemplates(newSelected, { onSuccess: onClose });
  };

  const filtered = pageStructures.filter((page) => {
    const matchesSearch = !search || page.name.toLowerCase().includes(search.toLowerCase());
    const matchesScope =
      scopeFilter === 'all' ||
      (scopeFilter === 'global' && !page.organization && !page.documentSuperId) ||
      (scopeFilter === 'organization' && page.organization && !page.documentSuperId) ||
      (scopeFilter === 'selected' && selected.includes(page.superId));
    return matchesSearch && matchesScope;
  });

  return (
    <Modal
      titleLabelKey={i18n('theme.structure.select-title')}
      show={show}
      onClose={onClose}
      size="xl"
      scrollable
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            {i18n('common.button.cancel')}
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSave}>
            {i18n('common.button.save')}
          </button>
        </>
      }
    >
      <div className="d-flex align-items-center gap-2 mb-3 sticky-top bg-blue-100 p-2 rounded">
        <input type="text" className="form-control" placeholder={i18n('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="form-select" style={{ width: 'auto' }} value={scopeFilter} onChange={(e) => setScopeFilter(e.target.value)}>
          <option value="all">{i18n('common.all')}</option>
          <option value="global">{i18n('common.global')}</option>
          <option value="organization">{i18n('common.organization')}</option>
          <option value="selected">{i18n('common.selected')}</option>
        </select>
      </div>
      {filtered.length === 0 ? (
        i18n('theme.structure.no-available')
      ) : (
        <div className="d-flex flex-wrap justify-content-start gap-r-2">
          {filtered.map((page) => (
            <div key={page.superId} className="mb-3">
              <StructurePreview
                onClick={() => handlePreviewClick(page.superId)}
                template={page}
                active={newSelected.includes(page.superId)}
                aspectRatio={aspectRatio}
              />
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

export const StructureList = () => {
  const { documentId } = useParams();
  const { selectedOrganization } = useContext(UserContext);
  const [showSelectModal, setShowSelectModal] = useState(false);
  const { data: selectedTemplates, isLoading: selectedTemplatesIsLoading } = useSelectedTemplates(documentId);
  const { documentPermissions } = usePermissions(documentId);
  const aspectRatio = useDocumentAspectRatio();
  const { data, isLoading } = useTemplates({
    organization: selectedOrganization,
    type: 'template',
    documentSuperId: documentId,
  });

  if (isLoading || selectedTemplatesIsLoading) {
    return <Spinner />;
  }

  const handleClickAddStructure = () => {
    setShowSelectModal(true);
  };
  const handleCloseSelectModal = () => {
    setShowSelectModal(false);
  };

  return (
    <>
      <div className="d-flex flex-row justify-content-between align-items-center mb-3">
        <h2 className="m-0">{i18n('theme.structure')}</h2>
        {(documentPermissions.includes('DESIGN') || documentPermissions.includes('CODER')) && (
          <button
            type="button"
            onClick={handleClickAddStructure}
            className="btn-clear rounded-circle aspect-1x1 bg-green-700 text-white d-flex justify-content-center align-items-center "
            style={{ height: '43px', fontSize: '15px' }}
          >
            <i className="fa-regular fa-ellipsis-vertical fa-center-fix"></i>
          </button>
        )}
      </div>
      {data.length === 0 ? (
        i18n('theme.structure.no-selected')
      ) : (
        <div className="d-flex flex-wrap justify-content-start gap-r-2">
          {data
            .filter((page) => selectedTemplates.includes(page.superId))
            .map?.((page) => (
              <div key={page.superId} className="mb-3">
                <StructurePreview template={page} aspectRatio={aspectRatio} />
              </div>
            ))}
        </div>
      )}
      <StructureModal pageStructures={data} selected={selectedTemplates} show={showSelectModal} onClose={handleCloseSelectModal} aspectRatio={aspectRatio} />
    </>
  );
};
